import { Controller } from "@hotwired/stimulus"

// Contrôleur pour afficher dynamiquement un warning si le délai minimum entre séances n'est pas respecté
export default class extends Controller {
  static values = { 
    defaultHours: { type: Number, default: 24 },     // 24 heures par défaut
    firstSessionHours: { type: Number, default: 48 } // 48 heures pour une première séance
  }
  static targets = [
    "clientField",
    "startDateField", 
    "startTimeField",
    "warningContainer"
  ]

  connect() {
    console.log("MinimumDelayWarning controller connected")
    
    // Vérifier immédiatement au chargement si un warning doit être affiché
    this.checkDelayWarning()
    
    // Vérifier plusieurs fois avec des délais croissants pour s'assurer que les champs sont bien remplis
    // (utile quand le formulaire a des valeurs pré-remplies ou des combobox qui mettent du temps à se remplir)
    const delays = [50, 150, 300, 500, 1000]
    delays.forEach(delay => {
      setTimeout(() => {
        this.checkDelayWarning()
      }, delay)
    })
    
    // Écouter les événements de changement dans tout le formulaire
    this.element.addEventListener('change', (event) => {
      console.log("Form change detected:", event.target.name, event.target.value)
      setTimeout(() => this.checkDelayWarning(), 50)
    })

    // Écouter les événements d'input pour une réactivité plus rapide
    this.element.addEventListener('input', (event) => {
      if (event.target.type === 'date' || event.target.type === 'time') {
        console.log("Date/time input detected:", event.target.name, event.target.value)
        setTimeout(() => this.checkDelayWarning(), 50)
      }
    })

    // Écouter spécifiquement les changements sur le champ client caché
    const clientHiddenField = this.element.querySelector('#client-combobox-hidden-input')
    if (clientHiddenField) {
      clientHiddenField.addEventListener('change', (event) => {
        console.log("Client hidden field changed:", event.target.value)
        setTimeout(() => this.checkDelayWarning(), 100)
      })
    }
  }

  checkDelayWarning() {
    console.log("Checking delay warning...")
    
    const clientId = this.getClientId()
    const startDateTime = this.getStartDateTime()

    if (!clientId || !startDateTime) {
      console.log("Missing client or start time, hiding warning")
      this.hideWarning()
      return
    }

    console.log(`Checking for client ${clientId} at ${startDateTime}`)

    // Faire un appel AJAX pour vérifier le délai minimum
    this.fetchLastSessionInfo(clientId, startDateTime)
  }

  getClientId() {
    // Méthode 1: Utiliser le target défini
    if (this.hasClientFieldTarget) {
      const value = this.clientFieldTarget.value
      console.log(`Client ID from target: ${value}`)
      if (value) return value
    }

    // Méthode 2: Chercher le champ caché client_id (utilisé par les combobox)
    const hiddenClientField = this.element.querySelector('input[name*="client_id"][type="hidden"]')
    if (hiddenClientField && hiddenClientField.value) {
      const value = hiddenClientField.value
      console.log(`Client ID from hidden field: ${value}`)
      return value
    }

    // Méthode 3: Chercher le select client_id standard
    const selectClientField = this.element.querySelector('select[name*="client_id"]')
    if (selectClientField && selectClientField.value) {
      const value = selectClientField.value
      console.log(`Client ID from select: ${value}`)
      return value
    }

    // Méthode 4: Fallback général sur tous les champs client_id
    const clientField = this.element.querySelector('[name*="client_id"]')
    if (clientField) {
      const value = clientField.value
      console.log(`Client ID from fallback: ${value}`)
      return value
    }

    console.log("No client ID found")
    return null
  }

  getStartDateTime() {
    let date = null
    let time = null

    // Récupérer la date
    if (this.hasStartDateFieldTarget) {
      date = this.startDateFieldTarget.value
    } else {
      const dateField = this.element.querySelector('[name*="start_date"]')
      if (dateField) {
        date = dateField.value
      }
    }

    // Récupérer l'heure
    if (this.hasStartTimeFieldTarget) {
      time = this.startTimeFieldTarget.value
    } else {
      const timeField = this.element.querySelector('[name*="start_time_only"]')
      if (timeField) {
        time = timeField.value
      }
    }

    if (!date || !time) {
      console.log(`Missing date (${date}) or time (${time})`)
      return null
    }

    // Combiner date et heure en format ISO
    const dateTime = `${date}T${time}:00`
    console.log(`Combined datetime: ${dateTime}`)
    return dateTime
  }

  async fetchLastSessionInfo(clientId, startDateTime) {
    try {
      const url = `/api/clients/${clientId}/last_sunbed_session_info?start_time=${encodeURIComponent(startDateTime)}`
      console.log(`Fetching: ${url}`)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        console.warn(`API call failed: ${response.status}`)
        this.hideWarning()
        return
      }

      const data = await response.json()
      console.log("API response:", data)

      if (data.has_delay_warning) {
        this.showWarning(data)
      } else {
        this.hideWarning()
      }

    } catch (error) {
      console.error("Error checking delay warning:", error)
      this.hideWarning()
    }
  }

  showWarning(data) {
    if (!this.hasWarningContainerTarget) {
      console.warn("No warning container target found")
      return
    }

    const nextAllowedDate = new Date(data.next_allowed_time)
    const nextAllowedFormatted = nextAllowedDate.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

    this.warningContainerTarget.innerHTML = `
      <div class="bg-amber-50 border border-amber-200 rounded-md p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-amber-800">
              Délai minimum recommandé non respecté
            </h3>
            <div class="mt-2 text-sm text-amber-700">
              <p>Ce client a eu sa dernière séance il y a moins de ${data.min_delay_hours} heures.</p>
              <p class="mt-1">Prochaine séance recommandée : <strong>${nextAllowedFormatted}</strong></p>
              <p class="mt-2 text-xs text-amber-600">⚠️ Vous pouvez quand même enregistrer ce rendez-vous, mais il est recommandé d'attendre.</p>
            </div>
          </div>
        </div>
      </div>
    `

    this.warningContainerTarget.classList.remove('hidden')
    console.log("Warning shown")
  }

  hideWarning() {
    if (this.hasWarningContainerTarget) {
      this.warningContainerTarget.classList.add('hidden')
      this.warningContainerTarget.innerHTML = ''
      console.log("Warning hidden")
    }
  }
}
